import { ChevronLeft, ChevronRight } from "lucide-react"

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  loading?: boolean
}

const TAP_SHELL = "inline-flex items-center justify-center min-w-[44px] min-h-[44px]"

const Pagination = ({ currentPage, totalPages, onPageChange, loading = false }: PaginationProps) => {
  const renderPaginationButtons = () => {
    const buttons = []
    const showLeftEllipsis = currentPage > 3
    const showRightEllipsis = currentPage < totalPages - 2

    buttons.push(
      <span key="prev" className={TAP_SHELL}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={loading || currentPage === 1}
          className="w-8 px-2 h-8 rounded-sm flex items-center justify-center border border-public-border bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-public-bg-hover transition-all duration-300"
          aria-label="Trang trước"
        >
          <ChevronLeft
            className={
              loading || currentPage === 1
                ? "text-public-fg-disabled font-normal w-4 h-3.5"
                : "text-black font-normal w-4 h-3.5"
            }
            strokeWidth="2px"
            size={16}
          />
        </button>
      </span>,
    )

    buttons.push(
      <span key={1} className={TAP_SHELL}>
        <button
          onClick={() => onPageChange(1)}
          disabled={loading}
          className={`w-8 px-3 h-8 rounded-sm flex items-center justify-center text-base font-semibold transition-all duration-300 leading-none ${
            currentPage === 1
              ? "bg-public-brand-cta text-white hover:bg-public-brand-cta-hover"
              : "bg-white border border-public-border-soft text-public-brand-cta hover:bg-public-bg-hover"
          }`}
          aria-label={`Trang 1`}
          aria-current={currentPage === 1 ? "page" : undefined}
        >
          1
        </button>
      </span>,
    )

    const Ellipsis = (key: string) => (
      <span
        key={key}
        aria-hidden="true"
        className="w-8 px-2 h-8 flex items-center justify-center text-base text-public-fg font-medium leading-[24.8px]"
      >
        …
      </span>
    )

    if (showLeftEllipsis) buttons.push(Ellipsis("left-ellipsis"))

    let start = Math.max(2, currentPage - 1)
    let end = Math.min(totalPages - 1, currentPage + 1)

    if (currentPage === 1) {
      end = Math.min(totalPages - 1, 2)
    }

    if (currentPage === totalPages && totalPages > 2) {
      start = Math.max(2, totalPages - 1)
    }

    for (let i = start; i <= end; i++) {
      if (i === 1 || i === totalPages) continue
      buttons.push(
        <span key={i} className={TAP_SHELL}>
          <button
            onClick={() => onPageChange(i)}
            disabled={loading}
            className={`w-8 px-3 h-8 rounded-sm flex items-center sm:font-semibold justify-center text-base font-bold transition-all duration-300 leading-none ${
              currentPage === i
                ? "bg-public-brand-cta text-white"
                : "bg-white border border-public-border-soft text-public-brand-cta hover:bg-public-bg-hover"
            }`}
            aria-label={`Trang ${i}`}
            aria-current={currentPage === i ? "page" : undefined}
          >
            {i}
          </button>
        </span>,
      )
    }

    if (showRightEllipsis) buttons.push(Ellipsis("right-ellipsis"))

    if (totalPages > 1) {
      buttons.push(
        <span key={totalPages} className={TAP_SHELL}>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={loading}
            className={`w-8 px-3 h-8 rounded-sm flex items-center justify-center text-base font-semibold transition-all duration-300 leading-none ${
              currentPage === totalPages
                ? "bg-public-brand-cta text-white hover:bg-public-brand-cta-hover"
                : "bg-white border border-public-border-soft text-public-brand-cta hover:bg-public-bg-hover"
            }`}
            aria-label={`Trang ${totalPages}`}
            aria-current={currentPage === totalPages ? "page" : undefined}
          >
            {totalPages}
          </button>
        </span>,
      )
    }

    buttons.push(
      <span key="next" className={TAP_SHELL}>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={loading || currentPage === totalPages}
          className="w-8 px-2 h-8 rounded-sm flex items-center justify-center text-black border border-public-border bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-public-bg-hover transition-all duration-300"
          aria-label="Trang sau"
        >
          <ChevronRight
            className={
              loading || currentPage === totalPages
                ? "text-public-fg-disabled font-normal w-4 h-3.5"
                : "text-black font-normal w-4 h-3.5"
            }
            strokeWidth="2px"
            size={16}
          />
        </button>
      </span>,
    )

    return buttons
  }

  if (totalPages <= 1) return null

  return (
    <nav aria-label="Phân trang" className="flex justify-center">
      <div className="flex items-center gap-1">{renderPaginationButtons()}</div>
    </nav>
  )
}

export default Pagination